import { PaginatedResponse } from 'clipsal-cortex-types/src/common/types';
import { roundNumber } from 'clipsal-cortex-utils/src/calculations/number-utils';

import { EnvironmentalImpact, FleetDataV1, FleetSystemHealthStatus } from '../../api/api-fleet-data';
import { SiteData, SiteStatus } from '../../api/api-site';
import { baseApi } from '../../app/baseApi';
import {
  Aggregation,
  DEFAULT_ENVIRONMENTAL_IMPACT,
  DEFAULT_SYSTEM_HEALTH_STATUS,
} from '../fleet-dashboard/fleet-dashboard-helpers';

// This function is used to transform the response data
// from the API to the format that the UI expects
const transformFleetData = (response: FleetDataV1) => {
  return {
    ...response,
    consumed: {
      ...response.consumed,
      data: response.consumed.data.map((d) => {
        return {
          ...d,
          x: d.x * 1000,
        };
      }),
    },
    produced: {
      ...response.produced,
      data: response.produced.data.map((d) => {
        return {
          ...d,
          x: d.x * 1000,
        };
      }),
    },
  };
};

export const fleetDashboardApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSiteCountBasedOnOperatingStatus: builder.query<FleetSystemHealthStatus, number>({
      query: (tenantId) => {
        let url = `/fleet/fleet_operating_status`;
        if (tenantId) url += `?tenant_id=${tenantId}`;
        return url;
      },
      providesTags: (_, __, tenantId) => [{ type: 'SiteCountBasedOnOperatingStatus', tenantId }],
    }),
    getLastUpdatedDate: builder.query<{ last_updated: string | null }, number>({
      query: (tenantId) => `/fleet/tenants/${tenantId}/fleet_energy_last_updated`,
      providesTags: (_, __, tenantId) => [{ type: 'LastUpdated', tenantId }],
    }),
    getSiteCountBasedOnCommissioningStatus: builder.query<
      PaginatedResponse<SiteData>,
      { commissioningStatus: SiteStatus; tenantId: number }
    >({
      query: ({ tenantId, commissioningStatus }) =>
        `/fleet/sites?limit=0&offset=0&commissioning_status=${commissioningStatus}${
          tenantId ? `&tenant_id=${tenantId}` : ''
        }`,
      providesTags: (_, __, { tenantId, commissioningStatus }) => [
        { type: 'SiteCountBasedOnCommissioningStatus', tenantId, commissioningStatus },
      ],
    }),
    getEnvironmentalImpact: builder.query<EnvironmentalImpact, number>({
      query: (tenantId) => `/fleet/tenants/${tenantId}/fleet_emissions`,
      providesTags: (_, __, tenantId) => [{ type: 'EnvironmentalImpact', tenantId }],
    }),
    getFleetEnergy: builder.query<
      FleetDataV1,
      { tenantId: number; startDate: string; endDate: string; aggregation: Aggregation }
    >({
      query: ({ tenantId, startDate, endDate, aggregation }) =>
        `/fleet/tenants/${tenantId}/fleet_energy?start_date=${startDate}&end_date=${endDate}&aggregation=${aggregation}`,
      providesTags: (_, __, values) => [{ type: 'FleetEnergy', ...values }],
      transformResponse: transformFleetData,
    }),
    getFleetPower: builder.query<FleetDataV1, { tenantId: number; startDate: string; endDate: string }>({
      query: ({ tenantId, startDate, endDate }) =>
        `/fleet/tenants/${tenantId}/fleet_power?from_datetime=${startDate}&to_datetime=${endDate}`,
      transformResponse: transformFleetData,
      providesTags: (_, __, values) => [{ type: 'FleetPower', ...values }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSiteCountBasedOnOperatingStatusQuery,
  useGetLastUpdatedDateQuery,
  useGetSiteCountBasedOnCommissioningStatusQuery,
  useGetFleetEnergyQuery,
  useGetFleetPowerQuery,
  useGetEnvironmentalImpactQuery,
} = fleetDashboardApi;

export const useGetSiteCountBasedOnOperatingStatus = (tenantId: number) => {
  const { data, ...rest } = useGetSiteCountBasedOnOperatingStatusQuery(tenantId);
  return { data: data ?? DEFAULT_SYSTEM_HEALTH_STATUS, ...rest };
};

export const useGetEnvironmentalImpact = (tenantId: number) => {
  const { data, ...rest } = useGetEnvironmentalImpactQuery(tenantId);
  return {
    ...(data
      ? {
          treesPlanted: roundNumber(data.tree_eq, 1),
          netImpact: roundNumber(data.net, 1),
          emittedCo2: roundNumber(data.emitted, 1),
          offsetCo2: roundNumber(data.offset, 1),
        }
      : DEFAULT_ENVIRONMENTAL_IMPACT),
    ...rest,
  };
};

export const useGetLastUpdatedDate = (tenantId: number) => {
  const { data, ...rest } = useGetLastUpdatedDateQuery(tenantId);
  return { lastUpdated: data?.last_updated ?? null, ...rest };
};

export const useGetSiteCountBasedOnCommissioningStatus = (tenantId: number) => {
  const {
    data: activeSites,
    isLoading: isLoadingActiveSites,
    isFetching: isFetchingActiveSites,
  } = useGetSiteCountBasedOnCommissioningStatusQuery({ commissioningStatus: 'ACTIVE', tenantId });
  const {
    data: pendingSites,
    isLoading: isLoadingPendingSites,
    isFetching: isFetchingPendingSites,
  } = useGetSiteCountBasedOnCommissioningStatusQuery({
    commissioningStatus: 'PENDING',
    tenantId,
  });
  return {
    numActiveSites: activeSites?.item_count ?? 0,
    numPendingSites: pendingSites?.item_count ?? 0,
    isLoadingSitesBasedOnCommissioningStatus: isLoadingActiveSites || isLoadingPendingSites,
    isFetchingSitesBasedOnCommissioningStatus: isFetchingActiveSites || isFetchingPendingSites,
  };
};
